import { ReactNode } from "react";
import { ExecutionModes, VerificationStatus } from "shared/types";
import { Loader } from "shared/components/Loader";
import { Section } from "views/applications-id-report/components/Section";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";

export const ReportFallbackSlice = ({ children }: { children: ReactNode }) => {
  const {
    applicationId,
    applicationStatus,
    applicationMeta,
    applicant,
    activeVerification,
    verificationData,
    verificationReportData,
    isLoading,
    isError
  } = useCombinedIncomeContext();

  if (isLoading) return <Loader />;

  if (isError)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <ReportException
          title="Failed to load income report"
          applicationStatus={applicationStatus}
          applicationId={applicationId || "0"}
        />
      </Section>
    );

  if (
    !verificationData ||
    !verificationReportData ||
    !Object.keys(verificationReportData).length ||
    !applicationMeta.data
  )
    return null;

  if (
    activeVerification?.status === VerificationStatus.Skipped &&
    activeVerification.settings?.execution_mode === ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={activeVerification.id}
        applicationId={applicationMeta.data.id}
        verificationStatus={activeVerification.status}
      />
    );

  if (activeVerification?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;

  if (activeVerification?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  return children;
};
